import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  publishedTime?: string;
  author?: string;
  /** Set true on any behind-login app page (dashboard, builders, billing, etc.) — these should never be indexed. */
  noindex?: boolean;
}

export default function SEO({
  title,
  description,
  keywords = 'WhatsApp bot, Zimbabwe, automation, business chat, customer service, AI chatbot, WhatsApp marketing',
  image = '/ai_image.webp',
  url,
  type = 'website',
  publishedTime,
  author = 'autopilot Team',
  noindex = false,
}: SEOProps) {
  const siteName = process.env.REACT_APP_COMPANY_NAME || 'autopilot';
  const fullTitle = `${title} | ${siteName}`;
  const siteUrl = process.env.REACT_APP_WEBSITE_URL || 'https://autopilot.co.zw';
  const currentUrl = url || (typeof window !== 'undefined' ? window.location.href : siteUrl);
  const fullImageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  const robotsContent = noindex ? 'noindex, nofollow' : 'index, follow';

  return (
    <Helmet>
      {/* Basic Metadata */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={currentUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_ZW" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />

      {/* Additional SEO */}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="geo.region" content="ZW" />
      <meta name="geo.placename" content="Harare" />
      <meta name="geo.position" content="-17.825166;31.033510" />
      <meta name="ICBM" content="-17.825166, 31.033510" />

      {/* Structured Data / JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": type === 'website' ? "WebSite" : "Organization",
          "name": siteName,
          "url": siteUrl,
          "logo": `${siteUrl}/logo.png`,
          "sameAs": [
            process.env.REACT_APP_FACEBOOK_URL || "https://www.facebook.com/autopilot",
            process.env.REACT_APP_TWITTER_URL || "https://twitter.com/autopilot",
            process.env.REACT_APP_LINKEDIN_URL || "https://www.linkedin.com/company/autopilot"
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "email": process.env.REACT_APP_COMPANY_EMAIL || "support@autopilot.co.zw",
            "contactType": "customer service",
            "availableLanguage": ["English"]
          }
        })}
      </script>

      {/* Favicon and App Icons */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/logo192.png" />
    </Helmet>
  );
}